import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { isEqual } from 'lodash-es';
import {
  resolveLeafKey,
  resolveObjectType,
  resolveObjectTypeLabel,
  resolveSelectionModeStates,
  isPatternTagType,
} from '@/dashboard/features/globalVariables/domain/treeVariable.utils';
import { queryClient } from '@/common/lib/queryClient';
import {
  getV7TreeGlobalVariablesDashboardsByDashboardId,
  getV7TreeGlobalVariablesDashboardsGetByDashboardId,
  getGetV7TreeGlobalVariablesDashboardsGetByDashboardIdQueryKey,
} from '@/api-client/metav8Over/queries/tree-global-variable-controller/tree-global-variable-controller';
import type {
  TreeTagResponseData,
  TreeParentTagItem,
  TreeGlobalFilterItem,
} from '@/api-client/metav8Over/model';
import type {
  TreeVarDepthItem,
  TreeVarFilterDataForView,
} from '@/dashboard/features/globalVariables/components/globalVarFilter/types/globalVarFilter.type';
import type { TreeVariable } from '@/dashboard/features/globalVariables/domain/treeVariable.types';
import type { SelectedTreeVariable } from '@/dashboard/features/globalVariables/domain/globalVariable.types';
import { convertResponseToTreeVariable } from '@/dashboard/features/globalVariables/services/adaptor';
import { useDashboardViewStore } from '../dashboard-view';
import { TREE_VARIABLE_TAG_TYPE } from '@/dashboard/features/globalVariables/domain/treeVariable.constants';

const parseRawSelected = (raw: string): { tagKey: string; tagType: string; tagValueId: string } => {
  const [tagKey, tagType, tagValueId] = raw.split('--');
  return { tagKey: tagKey ?? '', tagType: tagType ?? '', tagValueId: tagValueId ?? '' };
};

export const getTagDisplayName = (tag: TreeParentTagItem): string =>
  tag.alias || tag.name || tag.id || '';

export const buildParentTargetsById = (
  tags: TreeParentTagItem[],
): Record<string, { [key: string]: string }[]> => {
  const map: Record<string, { [key: string]: string }[]> = {};
  for (const tag of tags) {
    if (tag.id && tag.parentTargets?.length) {
      map[tag.id] = tag.parentTargets;
    }
  }
  return map;
};

export const extractTagId = (optionValue: string): string => {
  const parts = optionValue.split('--');
  return parts.length > 1 ? parts[parts.length - 1] : optionValue;
};

export const convertFilterItemToTagData = (
  filterItem: TreeGlobalFilterItem,
): TreeTagResponseData => {
  const tagData: TreeTagResponseData = {};
  if (!filterItem.globalFilterKeyValues) return tagData;
  for (const [key, values] of Object.entries(filterItem.globalFilterKeyValues)) {
    tagData[key] = values.map((v) => ({
      id: v.tagValueId,
      name: v.tagName,
      alias: v.alias,
      parentTargets: v.parentTargets,
    }));
  }
  return tagData;
};

interface FilterChildDepthOptions {
  bidirectional?: boolean;
  fromDepthIndex?: number;
}

export const filterChildDepthOptions = (
  treeVar: TreeVarFilterDataForView,
  options?: FilterChildDepthOptions,
) => {
  const startIndex = options?.fromDepthIndex ?? 0;
  for (let i = startIndex; i < treeVar.depths.length; i++) {
    const depth = treeVar.depths[i];
    const resolvedKey = resolveObjectType(treeVar.platform, depth.depthKey);

    // 부모 선택값 수집 (현재 노드보다 상위)
    const parentSelections: Record<string, Set<string>> = {};
    let hasAnyParentSelection = false;

    for (let j = 0; j < i; j++) {
      const parentDepth = treeVar.depths[j];
      const realSelections = parentDepth.selectedList.filter((v) => v !== '*');
      if (realSelections.length > 0) {
        const key = resolveObjectType(treeVar.platform, parentDepth.depthKey);
        parentSelections[key] = new Set(realSelections);
        hasAnyParentSelection = true;
      }
    }

    // 자식 선택값으로부터 유효한 현재 depth ID 집합 계산 (양방향 필터링)
    let childValidIds: Set<string> | null = null;
    let hasAnyChildSelection = false;

    if (options?.bidirectional !== false) {
      for (let j = i + 1; j < treeVar.depths.length; j++) {
        const childDepth = treeVar.depths[j];
        const realSelections = childDepth.selectedList.filter((v) => v !== '*');
        if (realSelections.length === 0) continue;

        const validIdsFromChild = new Set<string>();
        for (const selectedValue of realSelections) {
          const childTagId = extractTagId(selectedValue);
          const parentTargets = childDepth.parentTargetsById[childTagId];
          if (parentTargets) {
            for (const entry of parentTargets) {
              const parentId = entry[resolvedKey];
              if (parentId) {
                validIdsFromChild.add(parentId);
              }
            }
          }
        }

        // parentTargets가 없거나 현재 depth 키가 미포함이면 필터하지 않음 (부모→자식 방향과 일관)
        if (validIdsFromChild.size > 0) {
          hasAnyChildSelection = true;
          if (childValidIds === null) {
            childValidIds = validIdsFromChild;
          } else {
            const prev = childValidIds;
            childValidIds = new Set([...prev].filter((x) => validIdsFromChild.has(x)));
          }
        }
      }
    }

    if (!hasAnyParentSelection && !hasAnyChildSelection) {
      depth.options = depth.allOptions;
      continue;
    }

    depth.options = depth.allOptions.filter((option) => {
      const tagId = extractTagId(option.value);

      // 부모 제약 조건
      if (hasAnyParentSelection) {
        const parentTargets = depth.parentTargetsById[tagId];
        if (parentTargets?.length) {
          const matchesParent = Object.entries(parentSelections).every(
            ([depthResolvedKey, selectedIds]) =>
              parentTargets.some((entry) => {
                const entryValue = entry[depthResolvedKey];
                return entryValue !== undefined && selectedIds.has(entryValue);
              }),
          );
          if (!matchesParent) return false;
        }
      }

      // 자식 제약 조건 (양방향 필터링)
      if (childValidIds !== null && !childValidIds.has(tagId)) {
        return false;
      }

      return true;
    });

    if (depth.selectedList.length > 0) {
      const validValues = new Set(depth.options.map((o) => String(o.value)));
      depth.selectedList = depth.selectedList.filter((v) => validValues.has(v));
    }
  }
};

/**
 * 단일선택(SINGLE) depth 의 selectedList 는 값 1개만 유지한다.
 * preset/restore 가 다중값을 그대로 채운 경우 첫 값만 남겨, 단일 드롭다운 표시와 실제 필터/요청을 일치시킨다.
 * 호출부: store applyPresetSelections + Export 창(대시보드/리포트) restoreTreeVarSelections.
 */
export const enforceSingleSelectDepths = (treeVar: TreeVarFilterDataForView) => {
  for (const depth of treeVar.depths) {
    if (depth.selectMode === 'SINGLE' && depth.selectedList.length > 1) {
      depth.selectedList = depth.selectedList.slice(0, 1);
    }
  }
};

const pickFirstOptionValue = (depth: TreeVarDepthItem): string | undefined =>
  depth.options.find((o) => extractTagId(o.value) !== '*')?.value;

/**
 * 빈 단일선택(SINGLE) depth 의 초기값을 자동 세팅한다 (bottom-up).
 * anchor(가장 깊은 단일)를 채운 뒤, anchor 에 가까운 부모부터 자식 선택으로 좁혀진 부모 옵션의 첫 값을
 * 위로 한 단계씩 채운다 — 가까운 부모를 먼저 채워야 상위 부모가 연쇄적으로 좁혀진다. 기존 선택은 보존.
 */
export const applySingleSelectDefaults = (treeVar: TreeVarFilterDataForView) => {
  let anchorIndex = -1;
  for (let i = 0; i < treeVar.depths.length; i++) {
    if (treeVar.depths[i].selectMode === 'SINGLE') anchorIndex = i;
  }
  if (anchorIndex === -1) return;

  const anchor = treeVar.depths[anchorIndex];
  if (anchor.selectedList.length === 0) {
    // 하위(멀티 포함) 선택이 있으면 그에 맞게 anchor 옵션을 먼저 좁혀야 일치하는 첫 값을 고른다
    filterChildDepthOptions(treeVar, { bidirectional: true });
    const first = pickFirstOptionValue(anchor);
    if (!first) return;
    anchor.selectedList = [first];
  }

  for (let i = anchorIndex - 1; i >= 0; i--) {
    const parent = treeVar.depths[i];
    if (parent.selectMode !== 'SINGLE') continue;
    filterChildDepthOptions(treeVar, { bidirectional: true });
    if (parent.selectedList.length === 0) {
      const first = pickFirstOptionValue(parent);
      if (first) parent.selectedList = [first];
    }
  }

  filterChildDepthOptions(treeVar, { bidirectional: false });
};

/**
 * 부모 변경(top-down) 후, fromDepthIndex 이후의 빈 단일 depth 를 필터된 옵션의 첫 값으로 채운다.
 * 자식을 채울 때마다 그 아래 옵션을 재필터해 cascade 시킨다. 여전히 유효한 선택은 유지.
 */
export const fillEmptySingleChildren = (
  treeVar: TreeVarFilterDataForView,
  fromDepthIndex: number,
) => {
  for (let i = fromDepthIndex; i < treeVar.depths.length; i++) {
    const child = treeVar.depths[i];
    if (child.selectMode !== 'SINGLE' || child.selectedList.length > 0) continue;
    const first = pickFirstOptionValue(child);
    if (!first) continue;
    child.selectedList = [first];
    filterChildDepthOptions(treeVar, { bidirectional: false, fromDepthIndex: i + 1 });
  }
};

/**
 * depth 선택값 갱신 + 하위 옵션 재필터(부모→자식 단방향) + 무효화된 하위 단일 depth 재보강.
 * 런타임 소비자 3곳(store / Export 대시보드·리포트)의 depth 선택 갱신 공통 경로.
 * 상태 컨테이너(treeVar 조회)·추적 ID·sync 는 각 호출부가 담당한다.
 */
export const applyDepthSelection = (
  treeVar: TreeVarFilterDataForView,
  depthIndex: number,
  newItems: string[],
) => {
  treeVar.depths[depthIndex].selectedList = newItems;
  filterChildDepthOptions(treeVar, { bidirectional: false, fromDepthIndex: depthIndex + 1 });
  fillEmptySingleChildren(treeVar, depthIndex + 1);
};

export const convertTreeVariableToView = (
  variable: TreeVariable,
  tagData: TreeTagResponseData,
): TreeVarFilterDataForView => {
  const platform = variable.platform ?? '';
  const parentKeys = [variable.depth1, variable.depth2, variable.depth3, variable.depth4].filter(
    Boolean,
  ) as string[];
  const leafKey = variable.objectType;

  // 부모 depth: targetValue에 선택값이 있는 부모만 표시.
  // variable.depth1~4 는 raw('group'/'cluster'/'workload실제값'), targetValue.tagKey 는 resolved
  // (백엔드 약속, domain/SPEC.md). 매칭은 resolveObjectType 으로 raw → resolved 변환 후 비교.
  const targetValueKeys = new Set(variable.targetValue.map((v) => v.tagKey));
  const renderedParentKeys = parentKeys.filter((key) =>
    targetValueKeys.has(resolveObjectType(platform, key)),
  );

  // 저장된 singlePlatforms(own) → 렌더되는 부모 + leaf 사이에서 effective 선택 모드 derive
  // (하위 단일이 상위를 단일로 고정하는 cascade 를 런타임에도 동일 적용. leaf 도 최하위 depth 로 참여).
  const ownModes = Object.fromEntries(
    (variable.singlePlatforms ?? []).map((key) => [key, 'SINGLE' as const]),
  );
  const resolvedLeafKey = leafKey ? resolveLeafKey(platform, leafKey, variable.pathSubType) : '';
  const modeStates = resolveSelectionModeStates(
    [
      ...renderedParentKeys.map((key) => resolveObjectType(platform, key)),
      ...(leafKey ? [resolvedLeafKey] : []),
    ],
    ownModes,
  );

  const depths: TreeVarDepthItem[] = renderedParentKeys.map((key, index) => {
    const resolvedKey = resolveObjectType(platform, key);
    const tags = tagData[resolvedKey] ?? [];
    const options = tags.map((tag) => ({
      value: tag.id ?? '',
      name: getTagDisplayName(tag),
    }));
    return {
      depthKey: key,
      label: resolveObjectTypeLabel(platform, key),
      resolvedTagKey: resolvedKey,
      options,
      allOptions: options,
      parentTargetsById: buildParentTargetsById(tags),
      selectedList: [],
      loading: false,
      selectMode: modeStates[index].effective,
    };
  });

  // 리프 depth: 딕셔너리에서 리프 키로 접근.
  // leafKey(=variable.objectType) 는 응답 raw 또는 workload 실제값('deployment' 등). resolveLeafKey 로
  // 통일해 resolved 키 산출 (workloadType=variable.pathSubType 케이스 안전 처리).
  if (leafKey) {
    // resolvedLeafKey 는 위에서 계산(cascade derive 에 leaf 포함 위해 끌어올림)
    const leafTags = tagData[resolvedLeafKey] ?? [];
    const hasLeafWildcard = variable.targetValue.some(
      (tv) => tv.tagKey === resolvedLeafKey && tv.tagValueId === '*',
    );
    const needsLeafTags = variable.targetFilterType === 'PATTERN' || hasLeafWildcard;

    const leafTagNameMap = new Map(leafTags.map((tag) => [tag.id, getTagDisplayName(tag)]));
    // option value 첫 토큰은 resolved leaf 키로 통일 — preset round-trip 시
    // applyPresetSelections 가 `${tagKey(resolved)}--tag--${id}` 로 재조립하는 형식과
    // 일치해야 filterChildDepthOptions 의 validValues 매칭이 성공한다.
    // needsLeafTags=false 분기(line 235)도 이미 resolved tagKey 사용.
    const leafOptions = needsLeafTags
      ? leafTags.map((tag) => ({
          value: `${resolvedLeafKey}--tag--${tag.id}`,
          name: getTagDisplayName(tag),
        }))
      : variable.targetValue
          .filter(
            (tv) =>
              tv.tagValueId !== '*' &&
              tv.tagKey === resolvedLeafKey &&
              leafTagNameMap.has(tv.tagValueId),
          )
          .map(({ tagKey, tagType, tagValueId }) => ({
            value: `${tagKey}--${tagType}--${tagValueId}`,
            name: leafTagNameMap.get(tagValueId)!,
          }));

    depths.push({
      depthKey: leafKey,
      label: resolveObjectTypeLabel(platform, leafKey),
      resolvedTagKey: resolvedLeafKey,
      options: leafOptions,
      allOptions: leafOptions,
      parentTargetsById: buildParentTargetsById(leafTags),
      selectedList: [],
      loading: false,
      selectMode: modeStates[modeStates.length - 1].effective,
    });
  }

  return {
    id: variable.variableId,
    aliasName: variable.alias,
    operationType: variable.tagOperationMethod ?? 'OR',
    targetFilterType: variable.targetFilterType,
    use: true,
    platform: variable.platform,
    objectType: variable.objectType,
    depths,
  };
};

export const useTreeVariablesStore = defineStore('treeVariables', () => {
  const dashboardId = computed(() => useDashboardViewStore().monitoringDashboard.dashboardId);

  const treeVariables = ref<TreeVarFilterDataForView[]>([]);
  const rawTreeVariables = ref<TreeVariable[]>([]);
  const updatedTreeVariableId = ref<number>();

  const selectedTreeVariables = ref<SelectedTreeVariable[]>([]);

  const buildSelectedTreeVariables = (): SelectedTreeVariable[] =>
    treeVariables.value.map((v) => {
      const lastIndex = v.depths.length - 1;
      const manualTagType = TREE_VARIABLE_TAG_TYPE.MANUAL[v.operationType];
      const selectedList: SelectedTreeVariable['selectedList'] = [];

      // 부모·leaf 구분 없이 각 depth 의 선택값을 모두 수집 (root→leaf 순서, '*' 제외)
      for (let i = 0; i <= lastIndex; i++) {
        const depth = v.depths[i];
        for (const selectedValue of depth.selectedList) {
          if (selectedValue === '*') continue;
          if (i === lastIndex) {
            // leaf depth: raw "tagKey--tagType--id" 파싱 후 tagKey 를 resolvedTagKey 로 통일
            selectedList.push({ ...parseRawSelected(selectedValue), tagKey: depth.resolvedTagKey });
          } else {
            // 부모 depth: 선택값은 id 문자열, tagType 은 operationType 기준 manual
            selectedList.push({
              tagKey: depth.resolvedTagKey,
              tagType: manualTagType,
              tagValueId: selectedValue,
            });
          }
        }
      }

      return {
        treeVariableId: v.id,
        use: v.use,
        operationType: v.operationType,
        targetFilterType: v.targetFilterType,
        selectedList,
      };
    });

  const syncSelectedTreeVariables = () => {
    const next = buildSelectedTreeVariables();
    if (!isEqual(selectedTreeVariables.value, next)) {
      selectedTreeVariables.value = next;
    }
  };

  const updateTreeVarDepthSelected = (
    treeVarId: number,
    depthIndex: number,
    newItems: string[],
  ) => {
    const treeVar = treeVariables.value.find((v) => v.id === treeVarId);
    if (treeVar?.depths[depthIndex]) {
      updatedTreeVariableId.value = treeVarId;
      applyDepthSelection(treeVar, depthIndex, newItems);
      syncSelectedTreeVariables();
    }
  };

  const toggleTreeVarUse = (treeVarId: number, use: boolean) => {
    const treeVar = treeVariables.value.find((v) => v.id === treeVarId);
    if (treeVar) {
      updatedTreeVariableId.value = treeVarId;
      treeVar.use = use;
      syncSelectedTreeVariables();
    }
  };

  const fetchTreeVariables = async () => {
    const id = dashboardId.value;
    if (id === -1) return;

    try {
      const [treeVarResponse, filterDataResponse] = await Promise.all([
        getV7TreeGlobalVariablesDashboardsByDashboardId(id),
        getV7TreeGlobalVariablesDashboardsGetByDashboardId(id),
      ]);

      rawTreeVariables.value = (treeVarResponse.data ?? []).map(convertResponseToTreeVariable);

      const filterItems = filterDataResponse.data?.data ?? [];
      treeVariables.value = rawTreeVariables.value.map((variable) => {
        const filterItem = filterItems.find(
          (f: TreeGlobalFilterItem) => f.globalFilterId === variable.variableId,
        );
        const tagData = filterItem ? convertFilterItemToTagData(filterItem) : {};
        return convertTreeVariableToView(variable, tagData);
      });
      for (const treeVar of treeVariables.value) {
        applySingleSelectDefaults(treeVar);
      }
      syncSelectedTreeVariables();
    } catch {
      //
    }
  };

  const refreshTreeVarOptions = async (treeVarId: number) => {
    const treeVar = treeVariables.value.find((v) => v.id === treeVarId);
    if (!treeVar) return;

    const rawVar = rawTreeVariables.value?.find((v) => v.variableId === treeVarId);
    if (!rawVar) return;

    for (const depth of treeVar.depths) {
      depth.loading = true;
    }

    try {
      const params = { globalFilterId: treeVarId };
      const queryKey = getGetV7TreeGlobalVariablesDashboardsGetByDashboardIdQueryKey(
        dashboardId.value,
        params,
      );
      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          getV7TreeGlobalVariablesDashboardsGetByDashboardId(dashboardId.value, params),
      });
      const filterItems = response.data?.data ?? [];
      const filterItem = filterItems.find(
        (f: TreeGlobalFilterItem) => f.globalFilterId === treeVarId,
      );
      const tagData = filterItem ? convertFilterItemToTagData(filterItem) : {};

      const lastDepthIndex = treeVar.depths.length - 1;
      // leaf 키는 convertTreeVariableToView 와 동일하게 resolveLeafKey 로 계산해야
      // objectType='workload'(raw) + pathSubType='deployment' 같은 입력에서 'deployment'
      // 로 정상 resolve 된다. 부모 depth 는 resolveObjectType 그대로.
      const resolvedLeafKey = resolveLeafKey(
        treeVar.platform,
        treeVar.depths[lastDepthIndex]?.depthKey ?? '',
        rawVar.pathSubType,
      );
      const hasLeafWildcard = rawVar.targetValue.some(
        (tv) => tv.tagKey === resolvedLeafKey && tv.tagValueId === '*',
      );
      const needsLeafTags = rawVar.targetFilterType === 'PATTERN' || hasLeafWildcard;

      for (let i = 0; i < treeVar.depths.length; i++) {
        const depth = treeVar.depths[i];
        const isLeaf = i === lastDepthIndex;
        const resolvedKey = isLeaf
          ? resolvedLeafKey
          : resolveObjectType(treeVar.platform, depth.depthKey);
        const tags = tagData[resolvedKey] ?? [];

        let newOptions;
        if (!isLeaf) {
          newOptions = tags.map((tag) => ({
            value: tag.id ?? '',
            name: getTagDisplayName(tag),
          }));
        } else if (needsLeafTags) {
          // convertTreeVariableToView 와 동일한 형식: 첫 토큰은 resolved leaf 키
          newOptions = tags.map((tag) => ({
            value: `${resolvedLeafKey}--tag--${tag.id}`,
            name: getTagDisplayName(tag),
          }));
        } else {
          const tagNameMap = new Map(tags.map((tag) => [tag.id, getTagDisplayName(tag)]));
          newOptions = rawVar.targetValue
            .filter(
              (tv) =>
                tv.tagValueId !== '*' && tv.tagKey === resolvedKey && tagNameMap.has(tv.tagValueId),
            )
            .map(({ tagKey, tagType, tagValueId }) => ({
              value: `${tagKey}--${tagType}--${tagValueId}`,
              name: tagNameMap.get(tagValueId)!,
            }));
        }

        depth.options = newOptions;
        depth.allOptions = newOptions;
        depth.parentTargetsById = buildParentTargetsById(tags);
        depth.loading = false;
      }

      filterChildDepthOptions(treeVar, { bidirectional: false });
      // 재조회로 무효화되어 빈 값이 된 SINGLE depth 만 첫 옵션으로 재보강한다
      // (SINGLE 은 :clearable=false 라 항상 값 유지. MULTI 수동 clear 는 건드리지 않음)
      fillEmptySingleChildren(treeVar, 0);
      syncSelectedTreeVariables();
    } catch {
      for (const depth of treeVar.depths) {
        depth.loading = false;
      }
    }
  };

  const applyPresetSelections = (presetTreeFilters: SelectedTreeVariable[]) => {
    // 전체 교체 경로(preset 적용)이므로 추적 ID를 초기화한다 (standard-variables와 동일 규약).
    updatedTreeVariableId.value = undefined;
    for (const treeVar of treeVariables.value) {
      const presetFilter = presetTreeFilters.find((f) => f.treeVariableId === treeVar.id);

      // 모든 depth 선택값 초기화
      for (const depth of treeVar.depths) {
        depth.selectedList = [];
      }

      if (presetFilter) {
        const lastIndex = treeVar.depths.length - 1;
        for (let i = 0; i < treeVar.depths.length; i++) {
          const depth = treeVar.depths[i];
          if (i === lastIndex) {
            // 리프 depth: 리프 tagKey 와 일치하는 pattern 이 아닌 항목만 복원 (부모 항목 혼입 방지)
            depth.selectedList = presetFilter.selectedList
              .filter(
                ({ tagKey, tagType }) =>
                  tagKey === depth.resolvedTagKey && !isPatternTagType(tagType),
              )
              .map(({ tagKey, tagType, tagValueId }) => `${tagKey}--${tagType}--${tagValueId}`);
          } else {
            // 부모 depth: 일치하는 manual(non-pattern) 항목 복원.
            // preset.selectedList[].tagKey 는 resolved (buildSelectedTreeVariables 가 resolvedTagKey 사용),
            // depth.depthKey 는 raw 이므로 depth.resolvedTagKey 로 비교.
            depth.selectedList = presetFilter.selectedList
              .filter(
                ({ tagKey, tagType }) =>
                  tagKey === depth.resolvedTagKey && !isPatternTagType(tagType),
              )
              .map(({ tagValueId }) => tagValueId);
          }
        }
        treeVar.use = presetFilter.use;
      } else {
        treeVar.use = true;
      }

      // 단일선택 depth 는 값 1개만 유지 (preset 이 다중값을 가진 경우 첫 값만)
      enforceSingleSelectDepths(treeVar);

      filterChildDepthOptions(treeVar, { bidirectional: false });

      applySingleSelectDefaults(treeVar);
    }
    syncSelectedTreeVariables();
  };

  const resetSelectedValues = () => {
    for (const treeVar of treeVariables.value) {
      for (const depth of treeVar.depths) {
        depth.selectedList = [];
      }
      treeVar.use = true;
    }
    syncSelectedTreeVariables();
  };

  const resetState = () => {
    treeVariables.value = [];
    rawTreeVariables.value = [];
    updatedTreeVariableId.value = undefined;
    selectedTreeVariables.value = [];
  };

  const getTreeVariableById = (id: number) => treeVariables.value.find((v) => v.id === id);

  return {
    treeVariables,
    selectedTreeVariables,
    updatedTreeVariableId,
    fetchTreeVariables,
    getTreeVariableById,
    updateTreeVarDepthSelected,
    toggleTreeVarUse,
    refreshTreeVarOptions,
    applyPresetSelections,
    resetSelectedValues,
    resetState,
  };
});
